import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  Hand,
  Loader2,
  Lock,
  Play,
  ShieldCheck,
  Undo2,
  UserCog,
  Wrench,
} from 'lucide-react';
import {
  TicketStatus,
  canHold,
  canVerify,
  mayAssignWork,
  startWorkKind,
  summariseChecklist,
} from '@nbh/shared';
import type { ChecklistProgress } from '@nbh/shared';
import { api, type SessionUser, type TicketDetailResponse } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { RejectSheet } from './RejectSheet';
import { ProofSheet } from './ProofSheet';
import { ReassignSheet } from './ReassignSheet';

export interface TicketActionToolbarProps {
  ticket: TicketDetailResponse;
  onChanged: () => void;
}

type ActionKind =
  'claim' | 'hold' | 'resume' | 'resolve' | 'verify' | 'reject' | 'close' | 'reassign';

/**
 * The actions available on a ticket right now (SPEC.md §5.1.1).
 *
 * Buttons are derived from the ticket's state and the viewer's role rather than shown
 * and disabled: an action a technician can never take is noise on a small screen. The
 * one exception is Resolve, which stays visible but disabled with a reason when the
 * checklist is outstanding — hiding it would look like the app had lost the action.
 *
 * The server re-checks every one of these; this is convenience, not enforcement.
 */
export function TicketActionToolbar({ ticket, onChanged }: TicketActionToolbarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [busy, setBusy] = useState<ActionKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Resolve goes through a sheet, so the close-out photo can be offered (D33). */
  const [resolving, setResolving] = useState(false);
  /**
   * And so does Verify, for the same reason at the other end (R7). Sign-off used to fire
   * the transition straight off the button, which left the verification stage of the
   * gallery permanently empty — not because managers had nothing to photograph, but
   * because nothing ever asked them.
   */
  const [verifying, setVerifying] = useState(false);
  /** Reject goes through a sheet too — its reason is mandatory (D7). */
  const [rejecting, setRejecting] = useState(false);
  /** And Reassign, which needs a person picked before there is anything to send (D38). */
  const [reassigning, setReassigning] = useState(false);

  const progress = summariseChecklist(ticket.checklistItems);
  const blocked = progress.outstandingRequired > 0;
  // The same check the server enforces, not a lookalike. Gating on `hasAdminAuthority`
  // hid the action from FRONT_DESK, who may verify, and offered it to SUPER_ADMIN, whom
  // the server then refused.
  const isVerifier = canVerify(user?.role);
  // The same predicate the server checks, not a lookalike — the lesson of the verifier
  // drift this component was already fixed for once.
  const mayAssign = mayAssignWork(user?.role);

  async function run(kind: ActionKind, action: () => Promise<unknown>) {
    setBusy(kind);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('actions.failed'));
    } finally {
      setBusy(null);
    }
  }

  function holdWithReason() {
    // Mandatory on the server too; asking here saves a round trip and a 400.
    const reason = window.prompt(t('actions.holdPrompt'));
    if (!reason?.trim()) return;

    // TODO(offline-transitions): fetch-only by deliberate exception to Rule 4 — see docs/DEBT.md.
    void run('hold', () => api.holdTicket(ticket.id, reason.trim()));
  }

  const actions = buildActions(ticket, user, isVerifier, mayAssign);

  return (
    <div className="border-t border-slate-800 bg-slate-950/95 p-3">
      {ticket.checklistItems.length > 0 && <ChecklistBadge progress={progress} blocked={blocked} />}

      {error && (
        <p role="alert" className="mb-2 text-sm text-red-400">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {actions.map((action) => {
          const disabled = busy !== null || (action.kind === 'resolve' && blocked);

          return (
            <button
              key={action.kind}
              type="button"
              disabled={disabled}
              title={action.kind === 'resolve' && blocked ? t('actions.blockedHint') : undefined}
              onClick={() => {
                if (action.kind === 'hold') return holdWithReason();
                if (action.kind === 'resolve') return setResolving(true);
                if (action.kind === 'verify') return setVerifying(true);
                if (action.kind === 'reject') return setRejecting(true);
                if (action.kind === 'reassign') return setReassigning(true);
                void run(action.kind, () => action.call(ticket.id));
              }}
              className={`flex min-h-[56px] flex-1 items-center justify-center gap-2 rounded-xl px-4 text-base font-semibold disabled:opacity-40 ${action.tone}`}
            >
              {busy === action.kind ? (
                <Loader2 className="size-5 animate-spin" aria-hidden="true" />
              ) : (
                action.icon
              )}
              {t(action.labelKey)}
            </button>
          );
        })}
      </div>

      {resolving && (
        <ProofSheet
          stage="resolve"
          onCancel={() => setResolving(false)}
          onConfirm={async (proofAttachmentId) => {
            // TODO(offline-transitions): fetch-only by deliberate exception to Rule 4 — see docs/DEBT.md.
            await api.transitionTicket(ticket.id, 'RESOLVED', undefined, proofAttachmentId);
            setResolving(false);
            onChanged();
          }}
        />
      )}

      {verifying && (
        <ProofSheet
          stage="verify"
          onCancel={() => setVerifying(false)}
          onConfirm={async (proofAttachmentId) => {
            // No stage is sent: the server tags this VERIFICATION from the transition
            // itself, and would ignore a stage the client claimed.
            // TODO(offline-transitions): fetch-only by deliberate exception to Rule 4 — see docs/DEBT.md.
            await api.transitionTicket(ticket.id, 'VERIFIED', undefined, proofAttachmentId);
            setVerifying(false);
            onChanged();
          }}
        />
      )}

      {reassigning && (
        <ReassignSheet
          currentDepartment={ticket.department}
          currentAssignee={{ id: ticket.assigneeId, name: ticket.assigneeName }}
          onCancel={() => setReassigning(false)}
          onConfirm={async (assigneeId, reason) => {
            // TODO(offline-transitions): fetch-only by deliberate exception to Rule 4 — see docs/DEBT.md.
            await api.assignTicket(ticket.id, assigneeId, reason);
            setReassigning(false);
            onChanged();
          }}
        />
      )}

      {rejecting && (
        <RejectSheet
          onCancel={() => setRejecting(false)}
          onConfirm={async (reason) => {
            // Rejection is RESOLVED → IN_PROGRESS: the job goes back to the technician
            // who has it, not back to the pool.
            // TODO(offline-transitions): fetch-only by deliberate exception to Rule 4 — see docs/DEBT.md.
            await api.transitionTicket(ticket.id, 'IN_PROGRESS', reason);
            setRejecting(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function ChecklistBadge({ progress, blocked }: { progress: ChecklistProgress; blocked: boolean }) {
  const { t } = useTranslation();

  return (
    <p className={`mb-2 text-xs ${blocked ? 'text-amber-400' : 'text-slate-400'}`}>
      {t('actions.checklistProgress', {
        completed: progress.total - progress.outstandingRequired,
        total: progress.total,
      })}
      {/* Missing proof is reported, never a blocker — decision D12. */}
      {progress.missingProof > 0 &&
        ` · ${t('actions.missingProof', { count: progress.missingProof })}`}
    </p>
  );
}

interface Action {
  kind: ActionKind;
  labelKey: string;
  icon: React.ReactNode;
  tone: string;
  call: (ticketId: string) => Promise<unknown>;
}

/**
 * Only the transitions the machine actually permits from this state, plus the one action
 * that is not a transition at all.
 *
 * Reassignment is appended rather than folded into the switch because it depends on the
 * viewer's authority and not on the status — a manager may move a job at almost any point
 * in its life, and the state machine has nothing to say about it (D38).
 */
function buildActions(
  ticket: TicketDetailResponse,
  viewer: SessionUser | null,
  isVerifier: boolean,
  mayAssign: boolean,
): Action[] {
  return [
    ...transitionActions(ticket, viewer, isVerifier),
    ...assignAction(ticket.status, mayAssign),
  ];
}

/**
 * Claim, or Start work — or nothing.
 *
 * Gated on `startWorkKind`, the predicate the server enforces, imported from shared so
 * the two cannot disagree. This branch used to switch on status alone: every technician
 * saw Claim on every open job, tapped it on jobs that were somebody else's, and read
 * "Forbidden" — the server was right and the button was lying. Hiding it is not the
 * control (the server rule is unchanged); it only stops the UI promising what the server
 * will refuse, which is the verifier-role drift this component was fixed for once already.
 *
 * Two labels for one transition. "Claim" takes an unassigned job off the pool; "Start
 * work" begins a job somebody already handed you — you do not claim what you were given.
 * Same endpoint, and the word is what tells a technician in a corridor whether the job
 * is already theirs. No viewer yet (the session still resolving) offers nothing: an
 * authority check fails closed.
 */
function startWorkAction(ticket: TicketDetailResponse, viewer: SessionUser | null): Action[] {
  const kind = viewer ? startWorkKind(ticket, viewer) : null;
  if (!kind) return [];

  const starting = kind === 'start';
  return [
    {
      kind: 'claim',
      labelKey: starting ? 'actions.startWork' : 'actions.claim',
      icon: starting ? (
        <Play className="size-5" aria-hidden="true" />
      ) : (
        <Hand className="size-5" aria-hidden="true" />
      ),
      tone: 'bg-brookfields-green ring-1 ring-brookfields-gold/70 text-white',
      // TODO(offline-transitions): fetch-only by deliberate exception to Rule 4 — see docs/DEBT.md.
      call: (id) => api.transitionTicket(id, 'IN_PROGRESS'),
    },
  ];
}

/**
 * Moving the job to someone else, where it means something.
 *
 * The server permits this at every status but CLOSED and CANCELLED, deliberately — a
 * blanket rule is easier to explain than a curated one. The UI is narrower on purpose:
 * VERIFIED work is done, checked and awaiting only administrative closure, so the assignee
 * has no remaining role and the button would be clutter on a small screen.
 *
 * RESOLVED stays, because rejection returns the job to IN_PROGRESS still assigned (D36).
 * Re-pointing ownership before that happens is a real case, not a hypothetical one.
 */
function assignAction(status: TicketStatus, mayAssign: boolean): Action[] {
  const offered: TicketStatus[] = [
    TicketStatus.Open,
    TicketStatus.Escalated,
    TicketStatus.InProgress,
    TicketStatus.OnHold,
    TicketStatus.Resolved,
  ];
  if (!mayAssign || !offered.includes(status)) return [];

  return [
    {
      kind: 'reassign',
      labelKey: 'actions.reassign',
      icon: <UserCog className="size-5" aria-hidden="true" />,
      // Slate, not a colour: handing work on is administrative rather than an act on the
      // job itself, and it must not compete with Resolve for a gloved thumb.
      tone: 'bg-slate-700 text-slate-100',
      // Opens the sheet — a person has to be picked first, so there is no direct call.
      call: () => Promise.resolve(),
    },
  ];
}

/**
 * Hold and Resume, gated on `canHold` — the assignee, or anyone who may direct work —
 * which is the predicate the server enforces, imported from shared. These two used to
 * switch on status alone, so a manager viewing somebody else's job saw Hold, tapped it,
 * and read "already has this job"; and once the server admits managers, a status-only
 * button would offer Hold to a technician on a job that is not theirs. Either way the
 * button and the guard disagree, which is the verifier-role drift again.
 */
function holdAction(ticket: TicketDetailResponse, viewer: SessionUser | null): Action[] {
  if (!viewer || !canHold(ticket, viewer)) return [];

  return [
    {
      kind: 'hold',
      labelKey: 'actions.hold',
      icon: <Wrench className="size-5" aria-hidden="true" />,
      tone: 'bg-sky-700 text-white',
      call: () => Promise.resolve(),
    },
  ];
}

function resumeAction(ticket: TicketDetailResponse, viewer: SessionUser | null): Action[] {
  if (!viewer || !canHold(ticket, viewer)) return [];

  return [
    {
      kind: 'resume',
      labelKey: 'actions.resume',
      icon: <Play className="size-5" aria-hidden="true" />,
      tone: 'bg-amber-600 text-white',
      // TODO(offline-transitions): fetch-only by deliberate exception to Rule 4 — see docs/DEBT.md.
      call: (id) => api.resumeTicket(id),
    },
  ];
}

function transitionActions(
  ticket: TicketDetailResponse,
  viewer: SessionUser | null,
  isVerifier: boolean,
): Action[] {
  switch (ticket.status) {
    case TicketStatus.Open:
    case TicketStatus.Escalated:
      return startWorkAction(ticket, viewer);

    case TicketStatus.InProgress:
      return [
        ...holdAction(ticket, viewer),
        {
          kind: 'resolve',
          labelKey: 'actions.resolve',
          icon: <CheckCircle className="size-5" aria-hidden="true" />,
          tone: 'bg-emerald-600 text-white',
          call: (id) => api.transitionTicket(id, 'RESOLVED'),
        },
      ];

    case TicketStatus.OnHold:
      return resumeAction(ticket, viewer);

    // Completed work, waiting on a manager. Two ways out, and both belong here: a
    // verifier who can only accept is not verifying, they are rubber-stamping — which
    // is exactly what the client offered before Reject existed at all.
    //
    // Frontline staff cannot sign off their own work (SPEC.md §5.1.1).
    case TicketStatus.Resolved:
      return isVerifier
        ? [
            {
              kind: 'reject',
              labelKey: 'actions.reject',
              icon: <Undo2 className="size-5" aria-hidden="true" />,
              // Amber, not red: sending work back is ordinary and non-destructive, and
              // a red button teaches verifiers to avoid the one control that keeps
              // standards honest.
              tone: 'bg-amber-600 text-white',
              // Opens the sheet — the reason is mandatory, so there is no direct call.
              call: () => Promise.resolve(),
            },
            {
              kind: 'verify',
              labelKey: 'actions.verify',
              icon: <ShieldCheck className="size-5" aria-hidden="true" />,
              tone: 'bg-emerald-600 text-white',
              // Opens the sheet, which offers an optional photo — so there is no direct
              // call, the same shape Resolve and Reject already have.
              call: () => Promise.resolve(),
            },
          ]
        : [];

    // Verified, awaiting administrative closure. Without this case a verified job
    // offered no actions at all and sat where nobody could finish it.
    case TicketStatus.Verified:
      return isVerifier
        ? [
            {
              kind: 'close',
              labelKey: 'actions.close',
              icon: <Lock className="size-5" aria-hidden="true" />,
              tone: 'bg-slate-200 text-slate-900',
              // TODO(offline-transitions): fetch-only by deliberate exception to Rule 4 — see docs/DEBT.md.
              call: (id) => api.transitionTicket(id, 'CLOSED'),
            },
          ]
        : [];

    default:
      return [];
  }
}
